/** Rangos del libro → entidades tipadas. La fila de cabeceras decide qué columna es cuál. */
import type { Exercise, Measure, Question, Section } from '@sut/domain';
import { bool, dateKeyOf, int, isoOf, str } from './cells.js';
import type { Values, WorkbookRanges } from './schema.js';

export type SessionType = 'FLOOR' | 'NORMAL';

export interface Session {
  id: string;
  /** yyyy-MM-dd */
  date: string;
  floorCompletedAt: string;
  normalStartedAt: string;
  normalFinishedAt: string;
  sessionType: SessionType;
  rounds: number;
  durationSeconds: number;
  effort: number | null;
  activity: string;
  pain: boolean | null;
  painNote: string;
  note: string;
  nextDaySoreness: number | null;
  wantToReturn: boolean | null;
  recoveryLoggedAt: string;
  exerciseFeedback: string;
  routineSnapshot: string;
  warmupSeconds: number | null;
}

export interface WingChunPractice {
  id: string;
  date: string;
  form: string;
  startedAt: string;
  completedAt: string;
  durationSeconds: number | null;
  isDailyMinimum: boolean;
  source: string;
  outcome: string;
  note: string;
  createdAt: string;
}

export interface Workbook {
  sessions: Session[];
  /** Pares clave → valor de la pestaña Settings (valores como texto). */
  settings: Record<string, string>;
  wingChun: WingChunPractice[];
  /** false si el libro aún no tiene la pestaña WingChun (se crea al escribir la primera práctica). */
  hasWingChunTab: boolean;
  /** Rutina de la pestaña `Rutina`, o null si el libro aún no la tiene (se crea a partir de `Settings`). */
  routine: Exercise[] | null;
  /** Avisos de lectura (cabeceras que faltan, filas sin id…), para mostrar en la app. */
  warnings: string[];
}

type Row = unknown[];

/** Columna → índice, a partir de la fila de cabeceras. */
export function headerIndex(values: Values): Map<string, number> {
  const map = new Map<string, number>();
  (values[0] ?? []).forEach((h, i) => { const name = str(h); if (name && !map.has(name)) map.set(name, i); });
  return map;
}

function reader(values: Values) {
  const idx = headerIndex(values);
  return { idx, get: (row: Row, name: string): unknown => { const i = idx.get(name); return i === undefined ? '' : row[i]; } };
}

function missing(idx: Map<string, number>, required: readonly string[], tab: string): string[] {
  const absent = required.filter(h => !idx.has(h));
  return absent.length ? [`${tab}: faltan las columnas ${absent.join(', ')}`] : [];
}

export function parseSessions(values: Values, warnings: string[] = []): Session[] {
  if (!values.length) return [];
  const { idx, get } = reader(values);
  warnings.push(...missing(idx, ['id', 'date', 'sessionType'], 'Sessions'));
  const out: Session[] = [];
  let withoutId = 0;
  for (const row of values.slice(1)) {
    const id = str(get(row, 'id'));
    const date = dateKeyOf(get(row, 'date'));
    if (!id && !date) continue;
    if (!id) withoutId += 1;
    out.push({
      id,
      date,
      floorCompletedAt: isoOf(get(row, 'floorCompletedAt')),
      normalStartedAt: isoOf(get(row, 'normalStartedAt')),
      normalFinishedAt: isoOf(get(row, 'normalFinishedAt')),
      sessionType: str(get(row, 'sessionType')).toUpperCase() === 'NORMAL' ? 'NORMAL' : 'FLOOR',
      rounds: int(get(row, 'rounds')) ?? 0,
      durationSeconds: int(get(row, 'durationSeconds')) ?? 0,
      effort: int(get(row, 'effort')),
      activity: str(get(row, 'activity')),
      pain: bool(get(row, 'pain')),
      painNote: str(get(row, 'painNote')),
      note: str(get(row, 'note')),
      nextDaySoreness: int(get(row, 'nextDaySoreness')),
      wantToReturn: bool(get(row, 'wantToReturn')),
      recoveryLoggedAt: isoOf(get(row, 'recoveryLoggedAt')),
      exerciseFeedback: str(get(row, 'exerciseFeedback')),
      routineSnapshot: str(get(row, 'routineSnapshot')),
      warmupSeconds: int(get(row, 'warmupSeconds')),
    });
  }
  if (withoutId) warnings.push(`Sessions: ${withoutId} fila(s) sin id (se leen, pero no se pueden editar desde la app)`);
  return out;
}

export function parseSettings(values: Values): Record<string, string> {
  const out: Record<string, string> = {};
  for (const row of values.slice(1)) {
    const key = str(row[0]);
    if (key) out[key] = str(row[1]);
  }
  return out;
}

export function parseWingChun(values: Values, warnings: string[] = []): WingChunPractice[] {
  if (!values.length) return [];
  const { idx, get } = reader(values);
  warnings.push(...missing(idx, ['id', 'date', 'form'], 'WingChun'));
  const out: WingChunPractice[] = [];
  for (const row of values.slice(1)) {
    const id = str(get(row, 'id'));
    if (!id) continue;
    const completedAt = isoOf(get(row, 'completedAt'));
    out.push({
      id,
      date: dateKeyOf(get(row, 'date')),
      form: str(get(row, 'form')) || 'siu_nim_tau',
      startedAt: isoOf(get(row, 'startedAt')),
      completedAt,
      durationSeconds: int(get(row, 'durationSeconds')),
      isDailyMinimum: bool(get(row, 'isDailyMinimum')) ?? false,
      source: str(get(row, 'source')),
      outcome: str(get(row, 'outcome')),
      note: str(get(row, 'note')),
      createdAt: isoOf(get(row, 'createdAt')) || completedAt,
    });
  }
  return out;
}

const lower = (v: unknown) => str(v).toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

/** Lectura tolerante: admite que la pestaña se edite a mano en Sheets (p. ej. «segundos», «suelo», «Sí»). */
export function parseRoutine(values: Values, warnings: string[] = []): Exercise[] {
  if (!values.length) return [];
  const { idx, get } = reader(values);
  warnings.push(...missing(idx, ['id', 'section', 'name', 'target'], 'Rutina'));
  const out: Exercise[] = [];
  values.slice(1).forEach((row, i) => {
    const name = str(get(row, 'name'));
    const id = str(get(row, 'id'));
    if (!name && !id) return;
    const sec = lower(get(row, 'section'));
    const section: Section = sec === 'normal' ? 'NORMAL' : 'FLOOR';
    const m = lower(get(row, 'measure'));
    const measure: Measure = /seg|second/.test(m) ? 'seconds' : /lado|side/.test(m) ? 'reps_side' : 'reps';
    const q = lower(get(row, 'question'));
    const question: Question = /control|tecnica|lumbar/.test(q) ? 'control' : /done|hecho/.test(q) ? 'done' : 'rir';
    const target = int(get(row, 'target'));
    if (target === null || target <= 0) { warnings.push(`Rutina: «${name || id}» sin cantidad válida (se ignora)`); return; }
    out.push({
      id: id || name.toLowerCase().replace(/\s+/g, '_'),
      section,
      order: int(get(row, 'order')) ?? i + 1,
      name: name || id,
      measure,
      target,
      question,
      prompt: str(get(row, 'prompt')),
      active: bool(get(row, 'active')) ?? true,
    });
  });
  return out;
}

export function parseWorkbook(r: WorkbookRanges): Workbook {
  const warnings: string[] = [];
  return {
    sessions: parseSessions(r.sessions, warnings),
    settings: parseSettings(r.settings),
    wingChun: r.wingChun ? parseWingChun(r.wingChun, warnings) : [],
    hasWingChunTab: r.wingChun !== null,
    routine: r.routine ? parseRoutine(r.routine, warnings) : null,
    warnings,
  };
}
